import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Check Deposits RPC Contract
 *
 * A check deposit is a group of checks that goes to the bank as one deposit.
 * Check scans and remittances are dropped into a draft batch; each file is
 * read (checks by a vision model, remittances by the remittance pipeline),
 * checks are paired with their remittances, a person reviews the result, and
 * Submit turns every check into a cash receipt carrying the batch's id. The
 * receipts are then keyed into the ledger under one deposit reference.
 */

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^\d+(\.\d{1,2})?$/, {
  message: "Must be a positive amount with at most two decimals",
});
const DateOnlySchema = z.iso.date();

export const DepositBatchStatusSchema = z.enum(["draft", "submitted"]);
export const DepositBatchItemKindSchema = z.enum([
  "check",
  "remittance",
  "check_with_remittance",
  "other",
]);
export const DepositBatchItemProcessingStatusSchema = z.enum([
  "pending",
  "processing",
  "ready",
  "failed",
]);
export const DepositBatchItemPairingSourceSchema = z.enum(["auto", "user"]);

/** A remittance read from one of the batch's files: a candidate to pair a check with. */
export const DepositBatchRemittanceSchema = z.object({
  id: UUIDSchema,
  /** The batch file it was read from. */
  sourceItemId: UUIDSchema,
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: z.string().nullable(),
  statedReferenceNo: z.string().nullable(),
  statedPaymentDate: z.string().nullable(),
  /** The check it is paired with, if any. */
  pairedItemId: UUIDSchema.nullable(),
});

/** A workflow run, as much as a link to its page needs. */
export const DepositBatchRunLinkSchema = z.object({
  workflowId: UUIDSchema,
  workflowRunId: UUIDSchema,
});

/** Where a submitted check stands in the keying queue: its logging task. */
export const DepositBatchItemKeyingSchema = z.object({
  taskId: UUIDSchema,
  displayId: z.string().nullable(),
  status: z.string(),
  /** Raw outcome name; null while the task is open. */
  outcome: z.string().nullable(),
  /** Keyed into the ledger, here or directly. */
  keyed: z.boolean(),
});

/**
 * A cash receipt already in the books for a check of the deposit (from the
 * ledger, the bank feed, or an earlier deposit). Submit links the check and
 * its remittance to it instead of creating a second receipt.
 */
export const DepositBatchExistingReceiptSchema = z.object({
  id: UUIDSchema,
  originSystem: z.string().nullable(),
  transactionDate: z.string().nullable(),
  amount: z.string().nullable(),
  referenceNumber: z.string().nullable(),
});

export const DepositBatchItemSchema = z.object({
  id: UUIDSchema,
  documentId: UUIDSchema,
  fileName: z.string().nullable(),
  mimeType: z.string().nullable(),
  kind: DepositBatchItemKindSchema.nullable(),
  processingStatus: DepositBatchItemProcessingStatusSchema,
  processingError: z.string().nullable(),
  payerName: z.string().nullable(),
  checkAmount: z.string().nullable(),
  checkNumber: z.string().nullable(),
  checkDate: z.string().nullable(),
  micrRoutingNumber: z.string().nullable(),
  micrAccountNumber: z.string().nullable(),
  /** The written and numeric amounts on the check disagree: look at the image. */
  amountDisagrees: z.boolean(),
  remittanceAdviceIds: z.array(UUIDSchema),
  pairedRemittanceAdviceId: UUIDSchema.nullable(),
  pairingSource: DepositBatchItemPairingSourceSchema.nullable(),
  /** The cash receipt this check is: created by Submit, or the existing one. */
  bankTransactionId: UUIDSchema.nullable(),
  /** The check is already a cash receipt; null for one Submit creates. */
  existingReceipt: DepositBatchExistingReceiptSchema.nullable(),
  /** The receipt's logging task, once there is one. */
  keying: DepositBatchItemKeyingSchema.nullable(),
  /** The file's run of the deposit file workflow. */
  run: DepositBatchRunLinkSchema.nullable(),
  /** The remittance ingestion run for the file's remittance. */
  remittanceRun: DepositBatchRunLinkSchema.nullable(),
  createdAt: z.string(),
});

export const DepositBatchSummarySchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  status: DepositBatchStatusSchema,
  fileCount: z.number().int(),
  /** Files still being read. */
  processingCount: z.number().int(),
  checkCount: z.number().int(),
  /** Sum of the checks' amounts: the deposit total the bank scanner asks for. */
  checkTotal: z.string(),
  submittedAt: z.string().nullable(),
  /**
   * The ledger deposit reference every check is keyed under (Famous: Deposit
   * No). Null until the first check is loaded for keying.
   */
  depositReference: z.string().nullable(),
  createdAt: z.string(),
});

export const DepositBatchDetailSchema = DepositBatchSummarySchema.extend({
  items: z.array(DepositBatchItemSchema),
  remittances: z.array(DepositBatchRemittanceSchema),
  unpairedCheckCount: z.number().int(),
  /** Submitted checks keyed into the ledger so far. */
  keyedCheckCount: z.number().int(),
  /** Why Submit is refused right now. Empty when it is allowed. */
  submitBlockers: z.array(z.string()),
  /** The workflow new files run through (AR config); null until first use. */
  fileIntakeWorkflowId: UUIDSchema.nullable(),
});

export const UpdateDepositBatchItemInputSchema = z.object({
  id: UUIDSchema,
  itemId: UUIDSchema,
  kind: DepositBatchItemKindSchema.optional(),
  payerName: z.string().trim().max(255).nullable().optional(),
  checkAmount: DecimalStringSchema.nullable().optional(),
  checkNumber: z.string().trim().max(64).nullable().optional(),
  checkDate: DateOnlySchema.nullable().optional(),
  /** Null unpairs the check. */
  pairedRemittanceAdviceId: UUIDSchema.nullable().optional(),
});

const BatchResponseSchema = z.object({ batch: DepositBatchDetailSchema });

export const arDepositBatchesContracts = {
  list: oc
    .input(z.object({}))
    .output(z.object({ batches: z.array(DepositBatchSummarySchema) }))
    .route({
      method: "GET",
      summary: "List check deposits",
      description:
        "Lists the organization's check deposits, newest first, with file and check counts and the deposit total.",
    }),

  get: oc
    .input(z.object({ id: UUIDSchema }))
    .output(BatchResponseSchema)
    .route({
      method: "GET",
      summary: "Get a check deposit",
      description:
        "Returns one check deposit with every uploaded file, what was read off it and its workflow runs, the remittances read from the batch, check-to-remittance pairings, and anything blocking Submit. Reading a draft deposit picks up remittances its files' runs have recorded since.",
    }),

  create: oc
    .input(z.object({ name: z.string().trim().max(255).nullable().optional() }))
    .output(BatchResponseSchema)
    .route({
      method: "POST",
      summary: "Start a check deposit",
      description:
        "Creates an empty draft check deposit to drop check scans and remittances into.",
    }),

  addDocuments: oc
    .input(
      z.object({
        id: UUIDSchema,
        documentIds: z.array(UUIDSchema).min(1).max(200),
      }),
    )
    .output(BatchResponseSchema)
    .route({
      method: "POST",
      summary: "Add files to a check deposit",
      description:
        "Adds uploaded documents (check scans, remittances, or checks with remittance stubs) to a draft deposit and starts one run of the organization's deposit file workflow per document, creating that workflow on first use. A document already in the batch is skipped.",
    }),

  updateItem: oc
    .input(UpdateDepositBatchItemInputSchema)
    .output(BatchResponseSchema)
    .route({
      method: "POST",
      summary: "Correct a file in a check deposit",
      description:
        "Corrects what was read off one file of a draft deposit — its kind, the check's payer, amount, number or date — or pairs the check with a remittance from the batch (null unpairs it).",
    }),

  removeItem: oc
    .input(z.object({ id: UUIDSchema, itemId: UUIDSchema }))
    .output(BatchResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a file from a check deposit",
      description:
        "Removes one file from a draft deposit. Remittances already read from it stay in the remittance queue.",
    }),

  retry: oc
    .input(z.object({ id: UUIDSchema }))
    .output(BatchResponseSchema)
    .route({
      method: "POST",
      summary: "Re-read failed files",
      description:
        "Starts a new run of the deposit file workflow for every file of a draft deposit that failed. Checks paired with a failed file's remittances are unpaired first.",
    }),

  delete: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ id: UUIDSchema }))
    .route({
      method: "POST",
      summary: "Delete a draft check deposit",
      description:
        "Deletes a draft deposit and its files. Remittances already read from its files stay in the remittance queue. A submitted deposit cannot be deleted.",
    }),

  submit: oc
    .input(z.object({ id: UUIDSchema, depositDate: DateOnlySchema }))
    .output(
      BatchResponseSchema.extend({
        receiptIds: z.array(UUIDSchema),
        /** Checks that were already cash receipts, linked instead of created. */
        linkedReceiptIds: z.array(UUIDSchema),
      }),
    )
    .route({
      method: "POST",
      summary: "Submit a check deposit",
      description:
        "Locks a draft deposit and creates one cash receipt per check, dated the deposit date, each linked to its paired remittance. A check already in the books as a cash receipt (same check number and amount, or its remittance already linked to one) gets no second receipt: it and its remittance are linked to the existing one. Refused while files are still being read or a check is missing its amount, number or date.",
    }),
};

export type DepositBatchStatus = z.infer<typeof DepositBatchStatusSchema>;
export type DepositBatchItemKind = z.infer<typeof DepositBatchItemKindSchema>;
export type DepositBatchItem = z.infer<typeof DepositBatchItemSchema>;
export type DepositBatchRemittance = z.infer<
  typeof DepositBatchRemittanceSchema
>;
export type DepositBatchRunLink = z.infer<typeof DepositBatchRunLinkSchema>;
export type DepositBatchExistingReceipt = z.infer<
  typeof DepositBatchExistingReceiptSchema
>;
export type DepositBatchItemKeying = z.infer<
  typeof DepositBatchItemKeyingSchema
>;
export type DepositBatchSummary = z.infer<typeof DepositBatchSummarySchema>;
export type DepositBatchDetail = z.infer<typeof DepositBatchDetailSchema>;
export type UpdateDepositBatchItemInput = z.infer<
  typeof UpdateDepositBatchItemInputSchema
>;
